import i18next, { resources } from "@/i18n";
import { queryClient } from "@/integrations/tanstack-query/root-provider";
import { fieldInfoQueryOptions } from "@/services/field-info";

const STORAGE_CODES = ["en", "es"] as const;

/** Last org id we applied from GET /field-infos/localizations (undefined = unscoped bundle). */
let lastAppliedFieldInfoOrganizationId: string | undefined;

/**
 * Call after applying field-info localizations so `/_authenticated` beforeLoad can skip redundant
 * `ensureQueryData` + `applyAuthenticatedTranslations` on `cause: "stay"` (link preload / hover).
 */
export function markFieldInfoLocalizationsAppliedForOrganization(
	organizationId: string | undefined,
): void {
	lastAppliedFieldInfoOrganizationId = organizationId;
}

/** TanStack Router: parent matches stay mounted during `preloadRoute`, so `preload` is false but `cause` is `"stay"`. */
export function shouldSkipRedundantFieldInfoLocalizationsApply(
	resolvedOrganizationId: string | undefined,
	cause: unknown,
): boolean {
	if (cause !== "stay") return false;
	return resolvedOrganizationId === lastAppliedFieldInfoOrganizationId;
}

/**
 * Fetch GET /field-infos/localizations and replace the i18next `translation` namespace.
 * Call before navigating into `/_authenticated` (e.g. after OAuth) so the pending
 * spinner does not flash bootstrap English then API-locale copy.
 */
export async function ensureAuthenticatedI18n(
	organizationId?: string,
): Promise<void> {
	if (typeof window === "undefined") {
		return;
	}

	const raw = await queryClient.ensureQueryData(
		fieldInfoQueryOptions.localizations(
			organizationId ? { organizationId } : undefined,
		),
	);
	applyAuthenticatedTranslations(parseLocalizationsResponse(raw));
	markFieldInfoLocalizationsAppliedForOrganization(organizationId);
}

/** Attach API strings under codes i18next may look up (e.g. en-US). Resource store `added`/`removed` re-renders hooks (see i18n.ts react.bindI18nStore). */
function codesForApiBundle(): string[] {
	const codes = new Set<string>([...STORAGE_CODES]);
	const lng = i18next.language;
	if (lng && lng !== "cimode") {
		codes.add(lng);
		const base = lng.split("-")[0];
		if (base) codes.add(base);
	}
	return [...codes];
}

/**
 * Parse GET /field-infos/localizations body (nested JSON) for i18next.
 */
export function parseLocalizationsResponse(
	raw: unknown,
): Record<string, unknown> {
	if (raw === null || raw === undefined) {
		return {};
	}
	if (typeof raw === "string") {
		try {
			const parsed: unknown = JSON.parse(raw);
			if (
				typeof parsed === "object" &&
				parsed !== null &&
				!Array.isArray(parsed)
			) {
				return parsed as Record<string, unknown>;
			}
			return {};
		} catch {
			return {};
		}
	}
	if (typeof raw === "object" && !Array.isArray(raw)) {
		return raw as Record<string, unknown>;
	}
	return {};
}

/**
 * Replace `translation` with API-only copy. Human language is in the payload, not i18n lng.
 */
export function applyAuthenticatedTranslations(
	bundle: Record<string, unknown>,
): void {
	if (typeof window === "undefined") {
		return;
	}

	i18next.options.fallbackLng = false;
	i18next.options.returnEmptyString = true;
	i18next.options.parseMissingKeyHandler = () => "";

	for (const lng of codesForApiBundle()) {
		i18next.removeResourceBundle(lng, "translation");
		i18next.addResourceBundle(lng, "translation", bundle, true, true);
	}
}

/**
 * Restore bundled translation.json for login/public routes.
 */
export function restoreUnauthenticatedTranslations(): void {
	if (typeof window === "undefined") {
		return;
	}

	lastAppliedFieldInfoOrganizationId = undefined;
	i18next.options.fallbackLng = "en";
	i18next.options.returnEmptyString = false;
	delete i18next.options.parseMissingKeyHandler;

	for (const lng of STORAGE_CODES) {
		const staticBundle = resources[lng]?.translation;
		if (!staticBundle) {
			continue;
		}
		i18next.removeResourceBundle(lng, "translation");
		i18next.addResourceBundle(lng, "translation", staticBundle, true, true);
	}
}
